// src/store/wompi/wompiSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { WompiWebhookService } from "../../utils/wompi/api/webhook";
import { WompiService } from "../../utils/wompi/api/wompi.api";
import type {
  WompiWebhookPayload,
  WompiWebhookResponse,
  WompiApiResponse,
} from "../../utils/wompi/types/types";

export interface PendingPayment {
  amortizationId: number;
  transactionId?: string;
  amount: number;
  timestamp: number;
  paymentUrl?: string;
  status: "pending" | "checking" | "completed" | "failed" | "cancelled";
  customerEmail?: string;
  productName?: string;
}

interface WompiState {
  pendingPayments: Record<number, PendingPayment>;
  isProcessingWebhook: boolean;
  lastWebhookCheck: number | null;
  checkingPayments: number[];
  webhookHistory: Array<{
    timestamp: number;
    amortizationId: number;
    status: string;
    transactionId?: string;
  }>;
  error: string | null;
}

const initialState: WompiState = {
  pendingPayments: {},
  isProcessingWebhook: false,
  lastWebhookCheck: null,
  checkingPayments: [],
  webhookHistory: [],
  error: null,
};

// Async thunk para procesar webhook manualmente
export const processWebhookManually = createAsyncThunk(
  "wompi/processWebhookManually",
  async (payload: WompiWebhookPayload, { rejectWithValue }) => {
    try {
      const result = await WompiWebhookService.handleWebhook(payload);
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Error processing webhook"
      );
    }
  }
);

// Async thunk para verificar estado de pago
export const checkPaymentStatus = createAsyncThunk(
  "wompi/checkPaymentStatus",
  async (amortizationId: number, { rejectWithValue }) => {
    try {
      const result = await WompiService.getPaymentStatus(amortizationId);
      return { amortizationId, result };
    } catch (error) {
      return rejectWithValue({
        amortizationId,
        error:
          error instanceof Error
            ? error.message
            : "Error checking payment status",
      });
    }
  }
);

// Async thunk para verificar múltiples pagos pendientes
export const checkPendingPayments = createAsyncThunk(
  "wompi/checkPendingPayments",
  async (_, { getState, dispatch }) => {
    const state = getState() as { wompi: WompiState };
    const pendingIds = Object.keys(state.wompi.pendingPayments)
      .map((id) => parseInt(id))
      .filter((id) => state.wompi.pendingPayments[id].status === "pending");

    const results = await Promise.allSettled(
      pendingIds.map((id) => dispatch(checkPaymentStatus(id)))
    );

    return results;
  }
);

const wompiSlice = createSlice({
  name: "wompi",
  initialState,
  reducers: {
    // Agregar un pago pendiente
    addPendingPayment: (state, action) => {
      const payment: PendingPayment = action.payload;
      state.pendingPayments[payment.amortizationId] = {
        ...payment,
        timestamp: Date.now(),
        status: "pending",
      };
    },

    // Actualizar estado de pago
    updatePaymentStatus: (state, action) => {
      const { amortizationId, status, transactionId } = action.payload;
      if (state.pendingPayments[amortizationId]) {
        state.pendingPayments[amortizationId].status = status;
        if (transactionId) {
          state.pendingPayments[amortizationId].transactionId = transactionId;
        }
      }
    },

    // Remover pago pendiente (cuando se completa o falla definitivamente)
    removePendingPayment: (state, action) => {
      const amortizationId = action.payload;
      delete state.pendingPayments[amortizationId];

      // También remover de la lista de checking
      state.checkingPayments = state.checkingPayments.filter(
        (id) => id !== amortizationId
      );
    },

    // Limpiar pagos antiguos (más de 24 horas)
    cleanOldPendingPayments: (state) => {
      const now = Date.now();
      const dayInMs = 24 * 60 * 60 * 1000;

      Object.keys(state.pendingPayments).forEach((key) => {
        const amortizationId = parseInt(key);
        const payment = state.pendingPayments[amortizationId];

        if (now - payment.timestamp > dayInMs) {
          delete state.pendingPayments[amortizationId];
        }
      });
    },

    // Agregar entrada al historial de webhooks
    addWebhookHistoryEntry: (state, action) => {
      state.webhookHistory.unshift({
        timestamp: Date.now(),
        ...action.payload,
      });

      // Mantener solo los últimos 50 registros
      if (state.webhookHistory.length > 50) {
        state.webhookHistory = state.webhookHistory.slice(0, 50);
      }
    },

    // Limpiar error
    clearError: (state) => {
      state.error = null;
    },

    // Establecer último check de webhook
    setLastWebhookCheck: (state) => {
      state.lastWebhookCheck = Date.now();
    },
  },
  extraReducers: (builder) => {
    builder
      // Process webhook manually
      .addCase(processWebhookManually.pending, (state) => {
        state.isProcessingWebhook = true;
        state.error = null;
      })
      .addCase(processWebhookManually.fulfilled, (state, action) => {
        state.isProcessingWebhook = false;
        const result = action.payload as WompiWebhookResponse;

        if (result.processed && result.amortizationId && result.status) {
          // Actualizar estado del pago
          state.pendingPayments[result.amortizationId] = {
            ...state.pendingPayments[result.amortizationId],
            status: result.status as PendingPayment["status"],
          };

          // Agregar al historial
          state.webhookHistory.unshift({
            timestamp: Date.now(),
            amortizationId: result.amortizationId,
            status: result.status,
            transactionId:
              state.pendingPayments[result.amortizationId]?.transactionId,
          });

          // Si el pago se completó o falló, remover de pendientes después de un tiempo
          if (
            result.status === "paid" ||
            result.status === "failed" ||
            result.status === "cancelled"
          ) {
            setTimeout(() => {
              delete state.pendingPayments[result.amortizationId!];
            }, 5000); // 5 segundos de delay para mostrar el resultado
          }
        }

        state.lastWebhookCheck = Date.now();
      })
      .addCase(processWebhookManually.rejected, (state, action) => {
        state.isProcessingWebhook = false;
        state.error = action.payload as string;
      })

      // Check payment status
      .addCase(checkPaymentStatus.pending, (state, action) => {
        const amortizationId = action.meta.arg;
        if (!state.checkingPayments.includes(amortizationId)) {
          state.checkingPayments.push(amortizationId);
        }

        // Actualizar estado a checking
        if (state.pendingPayments[amortizationId]) {
          state.pendingPayments[amortizationId].status = "checking";
        }
      })
      .addCase(checkPaymentStatus.fulfilled, (state, action) => {
        const { amortizationId, result } = action.payload as {
          amortizationId: number;
          result: WompiApiResponse;
        };

        // Remover de checking
        state.checkingPayments = state.checkingPayments.filter(
          (id) => id !== amortizationId
        );

        // Actualizar estado basado en el resultado
        if (state.pendingPayments[amortizationId]) {
          if (result.success) {
            state.pendingPayments[amortizationId].status = "completed";
          } else {
            state.pendingPayments[amortizationId].status = "pending";
          }
        }
      })
      .addCase(checkPaymentStatus.rejected, (state, action) => {
        const payload = action.payload as {
          amortizationId: number;
          error: string;
        };

        // Remover de checking
        state.checkingPayments = state.checkingPayments.filter(
          (id) => id !== payload.amortizationId
        );

        // Mantener como pending si hay error
        if (state.pendingPayments[payload.amortizationId]) {
          state.pendingPayments[payload.amortizationId].status = "pending";
        }

        state.error = payload.error;
      });
  },
});

export const {
  addPendingPayment,
  updatePaymentStatus,
  removePendingPayment,
  cleanOldPendingPayments,
  addWebhookHistoryEntry,
  clearError,
  setLastWebhookCheck,
} = wompiSlice.actions;

export default wompiSlice.reducer;
